import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/**
 * /telemetry — hidden internal read-only ops dashboard.
 * Not linked from nav. Two sections:
 *   1. YouTube live + 30-day history
 *   2. EXP-0001 funnel (shorts vs standard + GA4 event roster)
 *
 * Read-only. No mutation buttons, no OAuth secrets, no refresh tokens.
 */

const API = process.env.REACT_APP_BACKEND_URL;

function useEndpoint(path) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetch(`${API}${path}`, { credentials: "omit" })
            .then(async (r) => {
                const body = await r.json().catch(() => ({}));
                if (cancelled) return;
                if (!r.ok) {
                    setError(body?.detail || `HTTP ${r.status}`);
                    setData(null);
                } else {
                    setError(null);
                    setData(body);
                }
            })
            .catch((e) => {
                if (!cancelled) {
                    setError(e.message || "network error");
                    setData(null);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [path]);
    return { data, error, loading };
}

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("en-US") : "—");
const fmtMinutes = (n) => (typeof n === "number" ? `${Math.round(n).toLocaleString()} min` : "—");
const fmtWhen = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toISOString().replace("T", " ").slice(0, 19) + "Z";
    } catch {
        return iso;
    }
};

/* ---------- Sparkline (dependency-free SVG) ---------- */
function Sparkline({ points, height = 140, label = "views" }) {
    if (!points || points.length === 0) {
        return (
            <div className="h-[140px] flex items-center justify-center text-xs uppercase tracking-[0.25em] text-zinc-300 border border-white/5">
                Waiting for daily snapshots
            </div>
        );
    }
    const w = 100; // viewBox width in %
    const h = 100;
    const ys = points.map((p) => p.value);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const rangeY = maxY - minY || 1;
    const path = points
        .map((p, i) => {
            const x = (i / Math.max(1, points.length - 1)) * w;
            const y = h - ((p.value - minY) / rangeY) * h * 0.85 - h * 0.075;
            return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
        })
        .join(" ");
    const area = `${path} L${w},${h} L0,${h} Z`;

    return (
        <div className="relative">
            <div className="absolute top-0 left-0 right-0 flex items-center justify-between text-[9px] uppercase tracking-[0.3em] text-zinc-300 pb-1 border-b border-white/5">
                <span>{label} · last {points.length} points</span>
                <span>
                    min {fmt(minY)} · max {fmt(maxY)}
                </span>
            </div>
            <svg
                viewBox={`0 0 ${w} ${h}`}
                preserveAspectRatio="none"
                height={height}
                className="w-full mt-4"
            >
                <defs>
                    <linearGradient id="spark-fill" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {/* Baseline grid */}
                <line x1="0" x2={w} y1={h * 0.5} y2={h * 0.5} stroke="#ffffff" strokeOpacity="0.06" strokeWidth="0.2" />
                <line x1="0" x2={w} y1={h * 0.9} y2={h * 0.9} stroke="#ffffff" strokeOpacity="0.06" strokeWidth="0.2" />
                <path d={area} fill="url(#spark-fill)" />
                <path d={path} fill="none" stroke="#22c55e" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
                {points.map((p, i) => {
                    const x = (i / Math.max(1, points.length - 1)) * w;
                    const y = h - ((p.value - minY) / rangeY) * h * 0.85 - h * 0.075;
                    return (
                        <circle key={i} cx={x} cy={y} r="0.5" fill="#22c55e" />
                    );
                })}
            </svg>
        </div>
    );
}

/* ---------- Stat cell ---------- */
function Stat({ label, value, sub }) {
    return (
        <div className="border border-white/20 bg-white/[0.04] p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-300">{label}</div>
            <div className="mt-3 font-display text-3xl md:text-4xl font-black tracking-tight text-white">
                {value}
            </div>
            {sub && (
                <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-zinc-300">{sub}</div>
            )}
        </div>
    );
}

/* ---------- Page ---------- */
export default function Telemetry() {
    const summary = useEndpoint("/api/telemetry/youtube/summary");
    const status = useEndpoint("/api/telemetry/status");
    const history = useEndpoint("/api/telemetry/youtube/history?days=30");
    const breakdown = useEndpoint("/api/telemetry/youtube/content-type-breakdown?days=28");

    useEffect(() => {
        document.title = "TELEMETRY — Rane Dynamics";
        // Discourage indexing
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex, nofollow";
        document.head.appendChild(meta);
        return () => {
            document.head.removeChild(meta);
        };
    }, []);

    const sparkPoints = useMemo(() => {
        const snaps = history.data?.snapshots || [];
        // Snapshots are newest-first — reverse for chart chronology
        const rev = [...snaps].reverse();
        return rev.map((s) => ({
            date: s.date,
            value: typeof s.last_28d_views === "number" ? s.last_28d_views : 0,
        }));
    }, [history.data]);

    const subsPoints = useMemo(() => {
        const snaps = history.data?.snapshots || [];
        const rev = [...snaps].reverse();
        return rev.map((s) => ({
            date: s.date,
            value: typeof s.subscribers === "number" ? s.subscribers : 0,
        }));
    }, [history.data]);

    // Content-type buckets
    const shorts = breakdown.data?.buckets?.find(
        (b) => (b.content_type || "").toLowerCase() === "shorts"
    );
    const standard = breakdown.data?.buckets?.find(
        (b) => (b.content_type || "").toLowerCase() === "videoondemand"
    );

    const s = summary.data || {};

    return (
        <div
            data-testid="telemetry-page"
            className="min-h-screen bg-[#0A0A0A] text-zinc-100"
            style={{ fontFamily: "'IBM Plex Mono','JetBrains Mono',ui-monospace,monospace" }}
        >
            <link
                rel="stylesheet"
                href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600&family=Outfit:wght@300;600;800;900&display=swap"
            />

            {/* Sticky header */}
            <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-11 flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                    <div className="flex items-center gap-4">
                        <span className="inline-flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                            RANE // TELEMETRY
                        </span>
                        <span className="hidden md:inline text-zinc-300">·</span>
                        <span className="hidden md:inline">
                            {status.data?.connected ? "OAUTH CONNECTED" : "OAUTH DISCONNECTED"}
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="hidden md:inline">READ-ONLY</span>
                        <span className="text-zinc-300">·</span>
                        <Link
                            to="/"
                            className="text-zinc-400 hover:text-white transition-colors"
                            data-testid="telemetry-back-link"
                        >
                            ← RANEDYNAMICS
                        </Link>
                    </div>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20 space-y-16 md:space-y-24">
                {/* ===================== SECTION A: YOUTUBE ===================== */}
                <section data-testid="tel-section-youtube">
                    <div className="flex items-center gap-4 mb-8">
                        <span className="text-[10px] uppercase tracking-[0.35em] text-zinc-300">
                            A · YOUTUBE
                        </span>
                        <span className="flex-1 h-px bg-white/10" />
                        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-300">
                            {status.data?.channel_title || s.channel_title || "Rane Dynamics"}
                        </span>
                    </div>

                    {summary.error && (
                        <div className="mb-6 border border-red-900/60 bg-red-950/20 text-red-300 p-4 text-xs">
                            <span className="uppercase tracking-[0.25em]">Summary unavailable:</span>{" "}
                            {summary.error}
                        </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                        <Stat
                            label="Subscribers"
                            value={summary.loading ? "…" : fmt(s.subscribers)}
                            sub="lifetime"
                        />
                        <Stat
                            label="Total Views"
                            value={summary.loading ? "…" : fmt(s.total_views)}
                            sub="lifetime"
                        />
                        <Stat
                            label="Total Videos"
                            value={summary.loading ? "…" : fmt(s.total_videos)}
                            sub="published"
                        />
                        <Stat
                            label="Views · 28d"
                            value={summary.loading ? "…" : fmt(s.last_28d_views)}
                            sub="rolling window"
                        />
                        <Stat
                            label="Watch Time · 28d"
                            value={
                                summary.loading
                                    ? "…"
                                    : fmtMinutes(s.last_28d_estimated_minutes_watched)
                            }
                            sub="estimated"
                        />
                        <Stat
                            label="Latest Snapshot"
                            value={
                                <span className="text-base md:text-lg font-mono">
                                    {history.data?.snapshots?.[0]?.captured_at
                                        ? fmtWhen(history.data.snapshots[0].captured_at)
                                        : summary.data?.fetched_at
                                          ? fmtWhen(summary.data.fetched_at)
                                          : "—"}
                                </span>
                            }
                            sub={`${history.data?.count || 0} in Mongo · 30d window`}
                        />
                    </div>

                    {/* Sparklines */}
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                        <div className="border border-white/20 bg-white/[0.04] p-5">
                            <Sparkline points={sparkPoints} label="28d views" />
                        </div>
                        <div className="border border-white/20 bg-white/[0.04] p-5">
                            <Sparkline points={subsPoints} label="subscribers" />
                        </div>
                    </div>

                    <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-zinc-300">
                        Chart draws from /api/telemetry/youtube/history?days=30 · daily snapshot at
                        ~08:00 UTC · history grows with time
                    </div>
                </section>

                {/* ===================== SECTION B: EXP-0001 ===================== */}
                <section data-testid="tel-section-exp">
                    <div className="flex items-center gap-4 mb-8">
                        <span className="text-[10px] uppercase tracking-[0.35em] text-zinc-300">
                            B · EXP-0001 // FUNNEL
                        </span>
                        <span className="flex-1 h-px bg-white/10" />
                        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-300">
                            LAND-0001
                        </span>
                    </div>

                    {/* Content type split */}
                    <div className="mb-8">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-300 mb-3">
                            SHORTS vs STANDARD · LAST 28 DAYS
                        </div>
                        {breakdown.error ? (
                            <div className="border border-red-900/60 bg-red-950/20 text-red-300 p-4 text-xs">
                                {breakdown.error}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                <Stat
                                    label="Shorts Views"
                                    value={breakdown.loading ? "…" : fmt(shorts?.views ?? 0)}
                                    sub={`${fmtMinutes(shorts?.watch_time_minutes ?? 0)} watched`}
                                />
                                <Stat
                                    label="Standard Views"
                                    value={breakdown.loading ? "…" : fmt(standard?.views ?? 0)}
                                    sub={`${fmtMinutes(standard?.watch_time_minutes ?? 0)} watched`}
                                />
                            </div>
                        )}
                    </div>

                    {/* GA4 event roster */}
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-300 mb-3">
                            GA4 EVENTS · TRACKED
                        </div>
                        <div className="border border-white/10">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[9px] uppercase tracking-[0.3em] text-zinc-300 border-b border-white/10">
                                        <th className="text-left px-4 py-3">Event</th>
                                        <th className="text-left px-4 py-3 hidden md:table-cell">
                                            Fires on
                                        </th>
                                        <th className="text-left px-4 py-3">Campaign</th>
                                        <th className="text-left px-4 py-3">Counts</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {[
                                        {
                                            event: "land_0001_view",
                                            fires: "/case-file/zero mount (500ms delay, visible tab)",
                                            campaign: "LAND-0001",
                                        },
                                        {
                                            event: "case_file_open",
                                            fires: "click OPEN CASE FILE / ACCESS GRANTED",
                                            campaign: "LAND-0001",
                                        },
                                        {
                                            event: "book_purchase_click",
                                            fires: "click READ NODE ZERO — $1 (Amazon)",
                                            campaign: "LAND-0001",
                                        },
                                        {
                                            event: "rane_dynamics_click",
                                            fires: "click EXPLORE RANE DYNAMICS →",
                                            campaign: "LAND-0001",
                                        },
                                    ].map((row) => (
                                        <tr
                                            key={row.event}
                                            data-testid={`ga4-event-row-${row.event}`}
                                            className="hover:bg-white/[0.02]"
                                        >
                                            <td className="px-4 py-3 font-mono text-zinc-100">
                                                {row.event}
                                            </td>
                                            <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">
                                                {row.fires}
                                            </td>
                                            <td className="px-4 py-3 text-zinc-400">
                                                {row.campaign}
                                            </td>
                                            <td className="px-4 py-3 text-zinc-300 text-[11px] uppercase tracking-[0.2em]">
                                                see GA4
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <p className="mt-4 text-xs text-zinc-300 max-w-2xl leading-relaxed">
                            Event counts live in Google Analytics 4 (property G-D67G1G77QW) —
                            open <b>Reports → Engagement → Events</b> or <b>Realtime → Events</b>{' '}
                            for outcome data. This dashboard intentionally keeps its OAuth
                            surface scoped to YouTube-only read scopes; funnel counts are
                            surfaced in GA4 rather than proxied through here.
                        </p>
                    </div>
                </section>

                {/* ===================== FOOTER ===================== */}
                <footer className="pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 text-[10px] uppercase tracking-[0.3em] text-zinc-300">
                    <span>READ-ONLY · NO MUTATIONS · NO SECRETS</span>
                    <span>
                        Data: /api/telemetry/* · Read scopes only ·{" "}
                        {status.data?.connected_at
                            ? `Connected ${fmtWhen(status.data.connected_at)}`
                            : "Not connected"}
                    </span>
                </footer>
            </main>
        </div>
    );
}
